import { BookOpenIcon, PenLineIcon, CalculatorIcon, FlaskConicalIcon, GlobeIcon, ClockIcon, UsersIcon, GraduationCapIcon, LightbulbIcon, HomeIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type Stat = {
  value: string
  label: string
}

export const STATS: Stat[] = [
  { value: 'COMPLETE', label: 'Visual Learning System' },
  { value: '4', label: 'Strategic Bonuses' },
  { value: 'FLEXIBLE', label: 'Multiple Ways To Use' },
  { value: 'WORLDWIDE', label: 'Digital Access' },
]

export const PROBLEMS: string[] = [
  'You’re constantly searching for better ways to explain concepts.',
  'Learning resources are scattered across websites, folders and downloads.',
  'Some materials contain too much text.',
  'Others are too basic or simply don’t hold a child’s attention.',
  'And every new topic means finding another resource.',
]

export type Category = {
  title: string
  items: string
  icon: LucideIcon
  accent: string
}

export const CATEGORIES: Category[] = [
  {
    title: 'Reading & Language',
    items: 'Phonics · Vocabulary · Words · Spelling · Reading Concepts',
    icon: BookOpenIcon,
    accent: 'bg-navy',
  },
  {
    title: 'Grammar & Writing',
    items: 'Sentences · Verbs · Nouns · Adjectives · Punctuation · Language Rules',
    icon: PenLineIcon,
    accent: 'bg-violet',
  },
  {
    title: 'Math',
    items: 'Numbers · Operations · Fractions · Geometry · Measurement · Time',
    icon: CalculatorIcon,
    accent: 'bg-green',
  },
  {
    title: 'Science',
    items: 'Animals · Human Body · Matter · Earth · Energy · Environment',
    icon: FlaskConicalIcon,
    accent: 'bg-orange',
  },
  {
    title: 'World & Geography',
    items: 'Maps · Continents · Oceans · Landforms · Climate · World Knowledge',
    icon: GlobeIcon,
    accent: 'bg-blue',
  },
  {
    title: 'Everyday Learning',
    items: 'Time · Money · Safety · Routines · Habits · Practical Skills',
    icon: ClockIcon,
    accent: 'bg-coral',
  },
]

export const USE_WAYS: string[] = [
  'Teach new concepts',
  'Explain visually',
  'Support homework',
  'Reinforce difficult topics',
  'Review previous learning',
  'Encourage independent learning',
  'Use at home, school or tutoring',
]

export type Audience = {
  title: string
  body: string
  icon: LucideIcon
  accent: string
}

export const AUDIENCES: Audience[] = [
  {
    title: 'Parents',
    body: 'Make homework and everyday learning easier.',
    icon: UsersIcon,
    accent: 'text-green',
  },
  {
    title: 'Teachers',
    body: 'Keep visual support ready when students need it.',
    icon: GraduationCapIcon,
    accent: 'text-blue',
  },
  {
    title: 'Tutors',
    body: 'Explain and reinforce concepts without constantly searching for resources.',
    icon: LightbulbIcon,
    accent: 'text-orange',
  },
  {
    title: 'Homeschoolers',
    body: 'Bring visual learning directly into everyday lessons.',
    icon: HomeIcon,
    accent: 'text-violet',
  },
]

export const EXPERIENCE_FLOW: { name: string; role: string; accent: string }[] = [
  { name: 'KIDS LEARNING READY™', role: 'See & Understand', accent: 'bg-navy' },
  { name: 'Learning Practice Lab™', role: 'Practice', accent: 'bg-green' },
  { name: 'Games & Learning Challenges™', role: 'Reinforce', accent: 'bg-orange' },
  { name: 'Real-Life Learning Missions™', role: 'Apply', accent: 'bg-blue' },
  { name: 'Smart Review & Progress System™', role: 'Review & Progress', accent: 'bg-violet' },
]

export const INCLUDED: string[] = [
  'Complete Visual Learning System',
  'Reading & Language Resources',
  'Grammar & Writing Resources',
  'Math Resources',
  'Science Resources',
  'World & Geography Resources',
  'Everyday Learning Resources',
  'Learning Practice Lab™',
  'Games & Learning Challenges™',
  'Real-Life Learning Missions™',
  'Smart Review & Progress System™',
  'Lifetime Access',
  'Instant Digital Delivery',
]

export const WITHOUT_SYSTEM: string[] = [
  'Constant searching',
  'Scattered learning materials',
  'Complicated explanations',
  'Resources used once and forgotten',
  'Starting from zero again',
]

export const WITH_SYSTEM: string[] = [
  'Everything organized',
  'Visual explanations ready',
  'Multiple learning areas',
  'Easier teaching and review',
  'Reusable resources',
  'Practical activities and games',
  'Real-life learning opportunities',
  'Organized review and progress',
]
